import jsPDF from 'jspdf';
import defaultLogo from '../assets/logo.png';
import logger from '../utils/logger';

// Helper function to format medication data
const formatMedicationData = (formData) => {
  const medicationSections = [];

  // Files medication
  const unmatchedFileMedications =
    Object.values(formData?.medicationdata?.extractedTextFromFiles || {})
      .flat()
      .flatMap(entry => entry.unmatchedMedications || []);

  const allMedicationsFromFiles = [
    ...(formData?.medicationlistfromfiles ? formData.medicationlistfromfiles.split(',') : []),
    ...unmatchedFileMedications
  ].filter(Boolean);

  if (allMedicationsFromFiles.length > 0) {
    medicationSections.push({
      title: 'Medications from Uploaded Files',
      data: allMedicationsFromFiles
    });
  }

  // Camera medication
  const unmatchedMedications =
    Object.values(formData?.medicationdata?.extractedTextFromCamera || {})
      .flat()
      .flatMap(entry => entry.unmatchedMedications || []);

  const allMedicationsFromCamera = [
    ...(formData?.medicationlistfromcamera ? formData.medicationlistfromcamera.split(',') : []),
    ...unmatchedMedications
  ].filter(Boolean);

  if (allMedicationsFromCamera.length > 0) {
    medicationSections.push({
      title: 'Medications from Camera',
      data: allMedicationsFromCamera
    });
  }

  // Search medication
  if (formData.medicationlistfromsearch && formData.medicationlistfromsearch.trim()) {
    medicationSections.push({
      title: 'Medications from Search',
      data: formData.medicationlistfromsearch
    });
  }

  // Voice Input medication
  if (formData.medicationvoiceinputlist && formData.medicationvoiceinputlist.trim()) {
    medicationSections.push({
      title: 'Medications from Voice Input',
      data: formData.medicationvoiceinputlist
    });
  }

  // Wizard medication
  if (formData.medicationlistfromwizard && formData.medicationlistfromwizard.trim()) {
    medicationSections.push({
      title: 'Medications from Wizard',
      data: formData.medicationlistfromwizard
    });
  }

  // Manual medications - check formatted string first, then array fallback
  const manualMedStr = formData.manualmedicationlist || formData.manualMedicationList;
  const manualMedArr = formData.manualMedications || formData.manualmedications;
  const manualMedData = manualMedStr
    || (manualMedArr && Array.isArray(manualMedArr) && manualMedArr.length > 0
      ? manualMedArr.map((med, i) => `${i + 1}. ${[med.type, med.generic, med.brand].filter(Boolean).join(' - ')}`).join(' | ')
      : null);
  if (manualMedData && manualMedData.trim()) {
    medicationSections.push({
      title: 'Medications (Manually Added)',
      data: manualMedData
    });
  }

  return medicationSections;
};

// Helper function to format medication allergies data
const formatAllergiesData = (formData) => {
  const allergiesSections = [];

  // Files allergies
  const unmatchedFileAllergies =
    Object.values(formData?.allergiesdata?.extractedTextFromFiles || {})
      .flat()
      .flatMap(entry => entry.unmatchedMedications || []);

  const allAllergiesFromFiles = [
    ...(formData?.allergieslistfromfiles ? formData.allergieslistfromfiles.split('\n') : []),
    ...unmatchedFileAllergies
  ].filter(Boolean);

  if (allAllergiesFromFiles.length > 0) {
    allergiesSections.push({
      title: 'Allergies from Uploaded Files',
      data: allAllergiesFromFiles
    });
  }

  // Voice Input allergies
  if (formData.allergiesvoiceinputlist && formData.allergiesvoiceinputlist.trim()) {
    allergiesSections.push({
      title: 'Allergies from Voice Input',
      data: formData.allergiesvoiceinputlist
    });
  }

  // Manual allergies - check formatted string first, then array fallback
  const manualAlrgStr = formData.manualallergieslist || formData.manualAllergiesList;
  const manualAlrgArr = formData.manualAllergies || formData.manualallergies;
  const manualAlrgData = manualAlrgStr
    || (manualAlrgArr && Array.isArray(manualAlrgArr) && manualAlrgArr.length > 0
      ? manualAlrgArr.map((med, i) => `${i + 1}. ${[med.type, med.generic, med.brand].filter(Boolean).join(' - ')}`).join(' | ')
      : null);
  if (manualAlrgData && manualAlrgData.trim()) {
    allergiesSections.push({
      title: 'Medication Allergies (Manually Added)',
      data: manualAlrgData
    });
  }

  return allergiesSections;
};

const formatPharmacyData = (formData) => {

  // Check multiple possible locations for pharmacy data
  let pharmacyData = null;

  // Location 1: pharmacydata.selectedPharmacy (new structure)
  if (formData?.pharmacydata?.selectedPharmacy) {
    pharmacyData = formData.pharmacydata.selectedPharmacy;
  }
  // Location 2: medicationdata.selectedPharmacy (old structure)
  else if (formData?.medicationdata?.selectedPharmacy) {
    pharmacyData = formData.medicationdata.selectedPharmacy;
  }
  // Location 3: Direct pharmacy fields
  else if (formData?.pharmacy_name || formData?.pharmacyname) {
    pharmacyData = {
      pharmacy_name: formData.pharmacy_name || formData.pharmacyname,
      shop_address: formData.shop_address || formData.pharmacyaddress,
      city: formData.city || formData.pharmacycity,
      zip_code: formData.zip_code || formData.pharmacyzip,
      contact_number: formData.contact_number || formData.pharmacyphone
    };
    logger.log('[PDF] Found pharmacy in direct fields');
  }

  if (pharmacyData && pharmacyData.pharmacy_name) {
    return {
      'Pharmacy Name': pharmacyData.pharmacy_name || 'N/A',
      'Address': pharmacyData.shop_address || 'N/A',
      'City': pharmacyData.city || 'N/A',
      'Zip Code': pharmacyData.zip_code || 'N/A',
      'Contact Number': pharmacyData.contact_number || 'N/A'
    };
  }
  
  // Fallback: Check if pharmacy data exists in pharmacyformatted field
  if (formData?.pharmacyformatted && formData.pharmacyformatted.trim()) {
    logger.log('[PDF] Found pharmacy in pharmacyformatted string');
    return {
      'Pharmacy Information': formData.pharmacyformatted
    };
  }
  
  logger.log('[PDF] No pharmacy data found in any location');
  return null;
};

const toLowercase = (str) => {
  if (!str || typeof str !== 'string') {
    return '';
  }
  
  return str
    .replace(/[^a-zA-Z0-9 ]/g, '') 
    .trim()
    .split(/\s+/) 
    .join('')     
    .toLowerCase();
};

/**
 * Generate PDF for patient assessment form
 * @param {Object} painLevelsData - Pain level data
 * @param {Object} formData - Form data
 * @param {Array} modules - Modules array
 * @param {Object} fieldsMap - Fields map
 * @param {Object} options - Additional options
 * @param {boolean} options.isFollowUp - Whether this is a follow-up submission
 * @param {string} options.patientName - Patient name for follow-up header
 * @param {string} options.logoUrl - Tenant logo URL (falls back to default if not provided or fails to load)
 */
export const generatePDF = (painLevelsData, formData, modules = null, fieldsMap = null, options = {}) => {
  const { isFollowUp = false, patientName = '', logoUrl = null } = options;

  return new Promise(async (resolve, reject) => {
    try {
      // Debug: Log all formData keys related to contacts/conditionals
      logger.log('🔍 ===== PDF GENERATION DEBUG =====');
      logger.log('📋 All formData keys:', Object.keys(formData));
      logger.log('📋 Contact-related keys:', Object.keys(formData).filter(k =>
        k.toLowerCase().includes('contact') || k.toLowerCase().includes('conditional')
      ));
      logger.log('📋 Contact field values:', Object.entries(formData)
        .filter(([k]) => k.toLowerCase().includes('contact'))
        .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
      );
      logger.log('📋 Is Follow-Up:', isFollowUp);
      logger.log('📋 Patient Name:', patientName);
      logger.log('🔍 ===== END DEBUG =====');

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [600, 780]
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 50;
      let cursorY = 75;

      // Load images - use tenant logo if provided, otherwise default
      let logoImg = null;
      if (logoUrl) {
        // Try tenant logo first
        logoImg = await loadImageToBase64(logoUrl);
      }
      // Fall back to default logo if tenant logo failed or not provided
      if (!logoImg) {
        logoImg = await loadImageToBase64(defaultLogo);
      }
      const bgImg = await loadImageToBase64('https://storage.googleapis.com/msgsndr/11NLpXQF1SZFaLK9l2gW/media/682c139b438303e8bc1af578.png');

      const drawWatermark = () => {
        const originalTextColor = doc.getTextColor();
        const originalFontSize = doc.getFontSize();
        const originalFont = doc.getFont();

        doc.setTextColor(230, 230, 230);
        doc.setFontSize(120);
        doc.setFont("Helvetica", "bold");
        doc.setTextColor(originalTextColor[0], originalTextColor[1], originalTextColor[2]);
        doc.setFontSize(originalFontSize);
        doc.setFont(originalFont.fontName, originalFont.fontStyle);
      };

      const drawHeader = () => {
        if (bgImg) doc.addImage(bgImg, 'PNG', 0, 0, 100, 100);
        if (logoImg) doc.addImage(logoImg, 'PNG', margin, 10, 100, 30);

        const now = new Date();
        const dateStr = `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`;
        const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        const fullText = `GENERATED ON: ${dateStr}, ${timeStr}`;

        doc.setFontSize(10);
        doc.setFont("Helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(fullText, pageWidth - margin, 25, { align: 'right' });

        const lineWidth = 120;
        const firstPartWidth = lineWidth * 0.3;
        doc.setDrawColor(54, 56, 69);
        doc.setLineWidth(2);
        doc.line(pageWidth - margin - lineWidth, 32, pageWidth - margin - lineWidth + firstPartWidth, 32);
        doc.setDrawColor(68, 145, 202);
        doc.line(pageWidth - margin - lineWidth + firstPartWidth, 32, pageWidth - margin, 32);

        // Show "PATIENT ASSESSMENT FORM" title for both regular and follow-up
        doc.setFontSize(20);
        doc.setFont("Helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("PATIENT ASSESSMENT ", pageWidth / 2 + 30, 50, { align: 'left' });
        doc.setTextColor(86, 164, 0);
        doc.text("FORM", pageWidth / 2 + 205, 50, { align: 'left' });
        // For follow-up, server adds "FOLLOW UP - Version X" banner at top center

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.setFont("Helvetica", "normal");
      };

      const drawSectionHeader = (text) => {
        if (cursorY > pageHeight - 100) {
          doc.addPage();
          drawWatermark();
          drawHeader();
          cursorY = 100;
        }

        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(0);
        doc.setLineWidth(0.2);
        doc.rect(margin, cursorY, pageWidth - margin * 2, 30, "FD");

        doc.setFillColor(0, 120, 200);
        doc.rect(margin, cursorY, 5, 30, "F");

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(15);
        doc.setFont("Helvetica", "bold");
        doc.text(text.toUpperCase(), margin + 20, cursorY + 20);

        cursorY += 50;
      };

      // Helper to check if value is a base64 image
      const isBase64Image = (value) => {
        // Check if it's an object with preview property (from some upload components)
        if (value && typeof value === 'object' && value.preview) {
          return isBase64Image(value.preview);
        }

        if (typeof value !== 'string') return false;

        // Check for data URL format
        if (value.startsWith('data:image/')) return true;

        // Check for raw base64 string (without data URI prefix)
        if (value.length > 100 && /^[A-Za-z0-9+/=]+$/.test(value.substring(0, 100))) return true;

        return false;
      };

      // Helper to extract base64 data from value
      const getBase64Data = (value) => {
        if (value && typeof value === 'object' && value.preview) {
          return value.preview;
        }
        return value;
      };

      // Helper to format date to mm/dd/yyyy
      const formatDateValue = (value, label) => {
        const labelLower = (label || '').toLowerCase();
        const isDateField = labelLower.includes('date') ||
                           labelLower.includes('dob') ||
                           labelLower.includes('birth');

        if (!isDateField) return value;

        // Try to parse the date
        let dateObj = null;

        if (typeof value === 'string') {
          // Check if it's an ISO date string or has time component
          if (value.includes('T') || value.includes(':')) {
            dateObj = new Date(value);
          } else if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // YYYY-MM-DD format
            dateObj = new Date(value + 'T00:00:00');
          } else if (value.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
            // Already in mm/dd/yyyy format, return as is
            return value;
          }
        } else if (value instanceof Date) {
          dateObj = value;
        }

        if (dateObj && !isNaN(dateObj.getTime())) {
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const year = dateObj.getFullYear();
          return `${month}/${day}/${year}`;
        }

        return value;
      };

      // Helper to get image format from base64 string
      const getImageFormat = (base64Value) => {
        if (base64Value.startsWith('data:image/jpeg') || base64Value.startsWith('data:image/jpg')) {
          return 'JPEG';
        } else if (base64Value.startsWith('data:image/png')) {
          return 'PNG';
        } else if (base64Value.startsWith('data:image/gif')) {
          return 'GIF';
        } else if (base64Value.startsWith('data:image/webp')) {
          return 'WEBP';
        }
        // Default to JPEG as it's most common
        return 'JPEG';
      };

      // Helper to add image to PDF
      const addImageToPdf = async (label, base64Value) => {
        const lineHeight = 20;
        const imageWidth = 150;
        const imageHeight = 100;

        // Check if we need a new page for label + image
        if (cursorY + lineHeight + imageHeight + 20 > pageHeight - 30) {
          doc.addPage();
          drawWatermark();
          drawHeader();
          cursorY = 100;
        }

        // Add label
        doc.setFont("Helvetica", "bold");
        doc.setFontSize(12);
        doc.text(`${label} :`, margin, cursorY);
        cursorY += lineHeight;

        try {
          // Get base64 data
          let imgData = getBase64Data(base64Value);

          if (!imgData || typeof imgData !== 'string') {
            throw new Error('Invalid image data');
          }

          // If it doesn't have data URI prefix, add it
          if (!imgData.startsWith('data:image/')) {
            imgData = 'data:image/jpeg;base64,' + imgData;
          }

          // Get the correct format
          const format = getImageFormat(imgData);

          // Use a promise to load the image first to ensure it's valid
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              try {
                doc.addImage(imgData, format, margin, cursorY, imageWidth, imageHeight);
                resolve();
              } catch (e) {
                reject(e);
              }
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = imgData;
          });

          cursorY += imageHeight + 15;
        } catch (err) {
          logger.error('Error adding image to PDF:', err);

          // Try with auto format detection
          try {
            let imgData = getBase64Data(base64Value);
            if (imgData && typeof imgData === 'string') {
              if (!imgData.startsWith('data:image/')) {
                imgData = 'data:image/png;base64,' + imgData;
              }
              doc.addImage(imgData, 'PNG', margin, cursorY, imageWidth, imageHeight);
              cursorY += imageHeight + 15;
            } else {
              throw new Error('Invalid image data');
            }
          } catch (err2) {
            logger.error('Error adding image to PDF (retry):', err2);
            doc.setFont("Helvetica", "normal");
            doc.text('[Image attached]', margin, cursorY);
            cursorY += lineHeight + 5;
          }
        }
      };

      const addLabelValue = async (label, value) => {
        if (shouldSkipField(label, value)) {
          return;
        }

        // Display as "Zip Code" even when the API label says "Postal Code"
        if (typeof label === 'string') {
          label = label.replace(/postal\s*code/gi, (m) =>
            m === m.toLowerCase() ? 'zip code' : m === m.toUpperCase() ? 'ZIP CODE' : 'Zip Code');
        }

        // Check if value is a base64 image
        if (isBase64Image(value)) {
          const base64Data = getBase64Data(value);
          await addImageToPdf(label, base64Data);
          return;
        }

        // Format date values
        value = formatDateValue(value, label);

        const lineHeight = 20;
        const indent = 150;
        const rightPadding = 30;
        const valueX = margin + indent;
        const valueMaxWidth = pageWidth - valueX - rightPadding;
        const labelMaxWidth = indent - 20;

        if (cursorY + lineHeight > pageHeight - 30) {
          doc.addPage();
          drawWatermark();
          drawHeader();
          cursorY = 100;
        }

        doc.setFont("Helvetica", "bold");
        doc.setFontSize(12);

        const labelText = `${label} :`;
        const labelWidth = doc.getTextWidth(labelText);

        if (labelWidth > labelMaxWidth) {
          const wrappedLabel = doc.splitTextToSize(labelText, pageWidth - margin * 2);

          wrappedLabel.forEach((line, index) => {
            if (cursorY + lineHeight > pageHeight - 30) {
              doc.addPage();
              drawWatermark();
              drawHeader();
              cursorY = 100;
            }
            doc.text(line, margin, cursorY);
            cursorY += lineHeight;
          });

          doc.setFont("Helvetica", "normal");

          let displayValue = "";
          if (Array.isArray(value)) {
            displayValue = value.length > 0 ? value.join(", ") : "N/A";
          } else if (value !== null && value !== undefined && value !== "") {
            displayValue = value.toString();
          } else {
            displayValue = "N/A";
          }

          const wrappedValueLines = doc.splitTextToSize(displayValue, pageWidth - margin * 2);

          wrappedValueLines.forEach((line, index) => {
            if (cursorY + lineHeight > pageHeight - 30) {
              doc.addPage();
              drawWatermark();
              drawHeader();
              cursorY = 100;
            }
            doc.text(line, margin + 0, cursorY);
            if (index < wrappedValueLines.length - 1) {
              cursorY += lineHeight;
            }
          });
        } else {
          doc.text(labelText, margin, cursorY);
          doc.setFont("Helvetica", "normal");

          let displayValue = "";
          if (Array.isArray(value)) {
            displayValue = value.length > 0 ? value.join(", ") : "N/A";
          } else if (value !== null && value !== undefined && value !== "") {
            displayValue = value.toString();
          } else {
            displayValue = "N/A";
          }

          const wrappedLines = doc.splitTextToSize(displayValue, valueMaxWidth);

          wrappedLines.forEach((line, index) => {
            if (index > 0 && cursorY + lineHeight > pageHeight - 30) {
              doc.addPage();
              drawWatermark();
              drawHeader();
              cursorY = 100;
            }
            doc.text(line, valueX, cursorY);
            if (index < wrappedLines.length - 1) {
              cursorY += lineHeight;
            }
          });
        }

        cursorY += lineHeight + 5;
      };

      const formatLabel = (key) => {
        if (!key) return '';
        return key
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .replace(/^./, str => str.toUpperCase());
      };

      const shouldSkipField = (label, value, fieldName = '') => {
        const labelLower = (label || '').toLowerCase();
        const valueLower = String(value || '').toLowerCase();
        const fieldNameLower = (fieldName || '').toLowerCase();

        // Skip accordion checkbox fields - they are handled separately
        // Pattern: *_section_*_checkbox_* or *section*checkbox* (after lowercase conversion removes underscores)
        if (fieldNameLower.match(/_section_\d+_checkbox_\d+/) || fieldNameLower.match(/section\d+checkbox\d+/)) {
          return true;
        }

        // Skip conditional input fields - they are handled with their parent contact fields
        // Pattern: *_conditional_*_* or *conditional** (with or without underscores)
        if (fieldNameLower.match(/_conditional_\d+_\d+/) ||
            fieldNameLower.match(/conditional\d+\d+/) ||
            fieldNameLower.match(/conditional\d\d/) ||
            fieldNameLower.includes('conditional')) {
          return true;
        }

        // Don't skip if it's a base64 image - we want to show images
        if (isBase64Image(value)) {
          return false;
        }

        const skipLabels = [
          't & c',
          'terms and conditions',
          'tc',
          'agree',
          'agreement',
          'medicationdata',
          'medication data',
          'allergiesdata',
          'allergies data'
        ];

        const skipValues = [
          'n/a',
          'na',
          'true',
          'false',
          ''
        ];

        if (skipLabels.some(skipLabel => labelLower.includes(skipLabel))) {
          return true;
        }

        if (skipValues.includes(valueLower)) {
          return true;
        }

        if (Array.isArray(value) && value.length === 0) {
          return true;
        }

        if (value === null || value === undefined || value === '') {
          return true;
        }

        return false;
      };

      // Helper to extract and format accordion data
      const formatAccordionData = (field, formDataValues) => {
        const accordionResults = [];
        const fieldName = toLowercase(field.labelName || field.field_name || field.name || '');

        if (!field.accordionSections) return accordionResults;

        field.accordionSections.forEach((section, sectionIndex) => {
          const sectionFieldName = `${fieldName}_section_${sectionIndex}`;
          const sectionData = {
            sectionTitle: section.title,
            checkedItems: []
          };

          section.checkboxes?.forEach((checkbox, checkboxIndex) => {
            const checkboxFieldName = `${sectionFieldName}_checkbox_${checkboxIndex}`;
            const isChecked = formDataValues[checkboxFieldName];

            if (isChecked) {
              const itemData = {
                label: checkbox.label,
                nestedValues: []
              };

              // Get nested input values
              checkbox.nestedInputs?.forEach((nestedInput, inputIndex) => {
                const nestedFieldName = `${checkboxFieldName}_input_${inputIndex}`;
                const nestedValue = formDataValues[nestedFieldName];
                if (nestedValue && nestedValue.toString().trim()) {
                  itemData.nestedValues.push({
                    label: nestedInput.label,
                    value: nestedValue
                  });
                }
              });

              sectionData.checkedItems.push(itemData);
            }
          });

          if (sectionData.checkedItems.length > 0) {
            accordionResults.push(sectionData);
          }
        });

        return accordionResults;
      };

      // Helper to scan formData keys and extract accordion data by pattern matching
      const extractAccordionDataFromFormKeys = (formDataValues, allFields) => {
        const accordionData = {};

        // Pattern with underscores: {fieldName}_section_{sectionIndex}_checkbox_{checkboxIndex}
        // Pattern without underscores (after convertFieldNameToLowercase): {fieldName}section{sectionIndex}checkbox{checkboxIndex}
        const accordionKeyPatternWithUnderscore = /^(.+)_section_(\d+)_checkbox_(\d+)$/;
        const accordionKeyPatternNoUnderscore = /^(.+)section(\d+)checkbox(\d+)$/;
        const nestedInputPatternWithUnderscore = /^(.+)_section_(\d+)_checkbox_(\d+)_input_(\d+)$/;
        const nestedInputPatternNoUnderscore = /^(.+)section(\d+)checkbox(\d+)input(\d+)$/;

        logger.log('🔍 Scanning formData for accordion patterns...');

        // First pass: find all checked checkboxes
        Object.entries(formDataValues).forEach(([key, value]) => {
          // Try both patterns
          let match = key.match(accordionKeyPatternWithUnderscore);
          if (!match) {
            match = key.match(accordionKeyPatternNoUnderscore);
          }

          if (match && value === true) {
            logger.log('✅ Found checked accordion checkbox:', key, 'value:', value);
            const [, baseFieldName, sectionIndex, checkboxIndex] = match;

            if (!accordionData[baseFieldName]) {
              accordionData[baseFieldName] = {};
            }
            if (!accordionData[baseFieldName][sectionIndex]) {
              accordionData[baseFieldName][sectionIndex] = {
                sectionTitle: '',
                checkedItems: []
              };
            }

            // Find section title and checkbox label from fields
            let sectionTitle = `Section ${parseInt(sectionIndex) + 1}`;
            let checkboxLabel = `Option ${parseInt(checkboxIndex) + 1}`;

            // Try to find the field definition to get actual titles/labels
            for (const fields of Object.values(allFields)) {
              for (const field of fields) {
                const fName = toLowercase(field.labelName || field.field_name || field.name || '');
                if (fName === baseFieldName && field.accordionSections) {
                  const section = field.accordionSections[parseInt(sectionIndex)];
                  if (section) {
                    sectionTitle = section.title;
                    const checkbox = section.checkboxes?.[parseInt(checkboxIndex)];
                    if (checkbox) {
                      checkboxLabel = checkbox.label;
                    }
                  }
                  break;
                }
              }
            }

            accordionData[baseFieldName][sectionIndex].sectionTitle = sectionTitle;
            accordionData[baseFieldName][sectionIndex].checkedItems.push({
              checkboxIndex,
              label: checkboxLabel,
              nestedValues: []
            });
          }
        });

        // Second pass: find nested input values
        Object.entries(formDataValues).forEach(([key, value]) => {
          // Try both patterns
          let match = key.match(nestedInputPatternWithUnderscore);
          if (!match) {
            match = key.match(nestedInputPatternNoUnderscore);
          }

          if (match && value && value.toString().trim()) {
            const [, baseFieldName, sectionIndex, checkboxIndex, inputIndex] = match;

            if (accordionData[baseFieldName]?.[sectionIndex]) {
              const checkedItem = accordionData[baseFieldName][sectionIndex].checkedItems.find(
                item => item.checkboxIndex === checkboxIndex
              );
              if (checkedItem) {
                // Find nested input label from field definition
                let nestedLabel = `Input ${parseInt(inputIndex) + 1}`;

                for (const fields of Object.values(allFields)) {
                  for (const field of fields) {
                    const fName = toLowercase(field.labelName || field.field_name || field.name || '');
                    if (fName === baseFieldName && field.accordionSections) {
                      const section = field.accordionSections[parseInt(sectionIndex)];
                      const checkbox = section?.checkboxes?.[parseInt(checkboxIndex)];
                      const nestedInput = checkbox?.nestedInputs?.[parseInt(inputIndex)];
                      if (nestedInput) {
                        nestedLabel = nestedInput.label;
                      }
                      break;
                    }
                  }
                }

                checkedItem.nestedValues.push({
                  label: nestedLabel,
                  value: value
                });
              }
            }
          }
        });

        return accordionData;
      };

      // Helper to add accordion data to PDF - Structured Contact Format
      const addAccordionToPdf = async (fieldLabel, accordionData) => {
        if (!accordionData || accordionData.length === 0) return;

        const lineHeight = 18;
        const labelValueLineHeight = 16;

        for (const section of accordionData) {
          // Check if this is an Emergency or Authorized Contacts section
          const isContactSection = section.sectionTitle &&
            (section.sectionTitle.toLowerCase().includes('emergency') ||
             section.sectionTitle.toLowerCase().includes('authorized') ||
             section.sectionTitle.toLowerCase().includes('contact'));

          if (isContactSection && section.checkedItems && section.checkedItems.length > 0) {
            // Render each contact as a separate block with its own heading
            section.checkedItems.forEach((item, itemIndex) => {
              // Skip if no nested values (all fields empty)
              if (!item.nestedValues || item.nestedValues.length === 0) {
                return;
              }

              // Check if all nested values are empty
              const hasNonEmptyValues = item.nestedValues.some(nested =>
                nested.value && nested.value.toString().trim() !== ''
              );
              if (!hasNonEmptyValues) {
                return;
              }

              // Page break check for contact heading
              if (cursorY + lineHeight * 2 > pageHeight - 30) {
                doc.addPage();
                drawWatermark();
                drawHeader();
                cursorY = 100;
              }

              // Contact heading (e.g., "Emergency Contact 1" or use item label)
              doc.setFont("Helvetica", "bold");
              doc.setFontSize(12);

              // Use item label as the contact heading (e.g., "Emergency Contact 1")
              const contactHeading = item.label || `${section.sectionTitle} ${itemIndex + 1}`;
              doc.text(contactHeading, margin, cursorY);
              cursorY += lineHeight;

              // Render each nested value as "Label: value"
              doc.setFont("Helvetica", "normal");
              doc.setFontSize(11);

              for (const nested of item.nestedValues) {
                // Skip empty values
                if (!nested.value || nested.value.toString().trim() === '') {
                  continue;
                }

                // Page break check
                if (cursorY + labelValueLineHeight > pageHeight - 30) {
                  doc.addPage();
                  drawWatermark();
                  drawHeader();
                  cursorY = 100;
                }

                const labelText = `${nested.label}: ${nested.value}`;
                const wrappedLines = doc.splitTextToSize(labelText, pageWidth - margin * 2 - 20);

                for (const line of wrappedLines) {
                  if (cursorY + labelValueLineHeight > pageHeight - 30) {
                    doc.addPage();
                    drawWatermark();
                    drawHeader();
                    cursorY = 100;
                  }
                  doc.text(line, margin + 10, cursorY);
                  cursorY += labelValueLineHeight - 2;
                }
              }

              // Add spacing between contact blocks
              cursorY += 12;
            });
          } else {
            // Non-contact accordion sections - use original format
            // Section title as question (bold)
            if (cursorY + lineHeight > pageHeight - 30) {
              doc.addPage();
              drawWatermark();
              drawHeader();
              cursorY = 100;
            }

            doc.setFont("Helvetica", "bold");
            doc.setFontSize(12);

            // Wrap section title if too long
            const sectionTitleLines = doc.splitTextToSize(section.sectionTitle, pageWidth - margin * 2);
            for (const line of sectionTitleLines) {
              if (cursorY + lineHeight > pageHeight - 30) {
                doc.addPage();
                drawWatermark();
                drawHeader();
                cursorY = 100;
              }
              doc.text(line, margin, cursorY);
              cursorY += lineHeight;
            }

            // Checked items - comma separated on same line
            const checkedLabels = section.checkedItems.map(item => item.label);
            if (checkedLabels.length > 0) {
              doc.setFont("Helvetica", "normal");
              doc.setFontSize(11);

              const checkedText = checkedLabels.join(', ');
              const wrappedChecked = doc.splitTextToSize(checkedText, pageWidth - margin * 2);

              for (const line of wrappedChecked) {
                if (cursorY + lineHeight > pageHeight - 30) {
                  doc.addPage();
                  drawWatermark();
                  drawHeader();
                  cursorY = 100;
                }
                doc.text(line, margin, cursorY);
                cursorY += lineHeight - 2;
              }

              // Nested values for each checked item
              for (const item of section.checkedItems) {
                if (item.nestedValues && item.nestedValues.length > 0) {
                  for (const nested of item.nestedValues) {
                    if (cursorY + lineHeight > pageHeight - 30) {
                      doc.addPage();
                      drawWatermark();
                      drawHeader();
                      cursorY = 100;
                    }

                    doc.setFont("Helvetica", "italic");
                    doc.setFontSize(10);
                    const nestedText = `${item.label} - ${nested.label}: ${nested.value}`;
                    const wrappedLines = doc.splitTextToSize(nestedText, pageWidth - margin * 2 - 30);
                    for (const line of wrappedLines) {
                      doc.text(line, margin + 20, cursorY);
                      cursorY += lineHeight - 4;
                    }
                  }
                }
              }
            }

            cursorY += 10;
          }
        }

        cursorY += 5;
      };

      drawWatermark();
      drawHeader();

      if (modules && modules.length > 0 && fieldsMap) {
        for (let moduleIndex = 0; moduleIndex < modules.length; moduleIndex++) {
          const module = modules[moduleIndex];
          const moduleName = module.module_name;
          const moduleFields = fieldsMap[moduleName] || [];
          // ✅ Special handling for Pharmacy module
          if (moduleName.toLowerCase().includes('pharmacy')) {
            const pharmacyData = formatPharmacyData(formData);

            if (pharmacyData) {
              drawSectionHeader("PHARMACY INFORMATION");

              for (const [label, value] of Object.entries(pharmacyData)) {
                await addLabelValue(label, value);
              }

              cursorY += 15;
            } else {
              logger.log("❌ No pharmacy data to add");
            }
            continue;
          }

          // ✅ Special handling for Medication Allergies module
          if (moduleName.toLowerCase().includes('medication') && moduleName.toLowerCase().includes('allergies')) {
            const allergiesSections = formatAllergiesData(formData);

            if (allergiesSections.length > 0) {
              drawSectionHeader("MEDICATION ALLERGIES");

              for (const section of allergiesSections) {
                const allergiesLines = Array.isArray(section.data)
                  ? section.data.filter(line => line && line.trim())
                  : section.data.split('\n').filter(line => line.trim());

                if (allergiesLines.length > 0) {
                  if (cursorY + 20 > pageHeight - 30) {
                    doc.addPage();
                    drawWatermark();
                    drawHeader();
                    cursorY = 100;
                  }

                  doc.setFont("Helvetica", "bold");
                  doc.setFontSize(12);
                  doc.text(`${section.title}:`, margin, cursorY);
                  cursorY += 15;

                  doc.setFont("Helvetica", "normal");
                  doc.setFontSize(12);

                  for (const line of allergiesLines) {
                    if (cursorY > pageHeight - 50) {
                      doc.addPage();
                      drawWatermark();
                      drawHeader();
                      cursorY = 100;
                    }

                    const wrappedLines = doc.splitTextToSize(line, pageWidth - margin * 2 - 20);
                    for (const wrappedLine of wrappedLines) {
                      doc.text(wrappedLine, margin + 0, cursorY);
                      cursorY += 10;
                    }
                  }

                  cursorY += 15;
                }
              }

              cursorY += 15;
            }
            continue;
          }

          // Special handling for Medication module (but not allergies)
          if (moduleName.toLowerCase().includes('medication') && !moduleName.toLowerCase().includes('allergies')) {
            const medicationSections = formatMedicationData(formData);

            if (medicationSections.length > 0) {
              drawSectionHeader("MEDICATION ENTRY");

              for (const section of medicationSections) {
                const medicationLines = Array.isArray(section.data)
                  ? section.data.filter(line => line && line.trim())
                  : section.data.split('\n').filter(line => line.trim());

                if (medicationLines.length > 0) {
                  if (cursorY + 20 > pageHeight - 30) {
                    doc.addPage();
                    drawWatermark();
                    drawHeader();
                    cursorY = 100;
                  }

                  doc.setFont("Helvetica", "bold");
                  doc.setFontSize(12);
                  doc.text(`${section.title}:`, margin, cursorY);
                  cursorY += 15;

                  doc.setFont("Helvetica", "normal");
                  doc.setFontSize(12);

                  for (const line of medicationLines) {
                    if (cursorY > pageHeight - 50) {
                      doc.addPage();
                      drawWatermark();
                      drawHeader();
                      cursorY = 100;
                    }

                    const wrappedLines = doc.splitTextToSize(line, pageWidth - margin * 2 - 20);
                    for (const wrappedLine of wrappedLines) {
                      doc.text(wrappedLine, margin + 0, cursorY);
                      cursorY += 10;
                    }
                  }

                  cursorY += 15;
                }
              }

              cursorY += 15;
            }
            continue;
          }

          const hasData = moduleFields.some(field => {
            const fieldName = toLowercase(field.labelName || field.field_name || field.name || '');
            if (!fieldName) return false;

            // Check if this field has accordion data in formData by checking for pattern
            const hasAccordionDataInForm = Object.keys(formData).some(key => {
              const withUnderscore = new RegExp(`^${fieldName}_section_\\d+_checkbox_\\d+$`);
              const noUnderscore = new RegExp(`^${fieldName}section\\d+checkbox\\d+$`);
              return withUnderscore.test(key) || noUnderscore.test(key);
            });

            // Check accordion fields for data
            if (field.fieldType === 'accordion' || field.type === 'accordion' || field.accordionSections || hasAccordionDataInForm) {
              if (field.accordionSections) {
                const accordionData = formatAccordionData(field, formData);
                return accordionData && accordionData.length > 0;
              }
              return hasAccordionDataInForm;
            }

            // Check address fields (composite field with subfields)
            if (field.type === 'address' || field.fieldType === 'address') {
              const hasAddressData = formData[`${fieldName}_street`] || formData[`${fieldName}street`] ||
                                     formData[`${fieldName}_city`] || formData[`${fieldName}city`] ||
                                     formData[`${fieldName}_state`] || formData[`${fieldName}state`];
              return !!hasAddressData;
            }

            const value = formData[fieldName];
            const hasValue = value !== null && value !== undefined && value !== "" &&
                           (!Array.isArray(value) || value.length > 0);
            return hasValue;
          });

          if (hasData || moduleFields.length > 0) {
            let displayModuleName = moduleName
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase());

            if (moduleIndex === 0 && moduleName.toLowerCase().includes('patient')) {
              displayModuleName = "PATIENT ONBOARDING DETAILS";
            }

            drawSectionHeader(displayModuleName);

            for (const field of moduleFields) {
              const fieldName = toLowercase(field.labelName || field.field_name || field.name || '');

              if (!fieldName) {
                logger.warn('Skipping field with no name:', field);
                continue;
              }

              // Check if this field has accordion data in formData by checking for pattern
              // Pattern: {fieldName}section{num}checkbox{num} or {fieldName}_section_{num}_checkbox_{num}
              const hasAccordionDataInForm = Object.keys(formData).some(key => {
                const withUnderscore = new RegExp(`^${fieldName}_section_\\d+_checkbox_\\d+$`);
                const noUnderscore = new RegExp(`^${fieldName}section\\d+checkbox\\d+$`);
                return withUnderscore.test(key) || noUnderscore.test(key);
              });

              // Handle accordion fields - either by accordionSections property or by checking formData keys
              if (field.fieldType === 'accordion' || field.type === 'accordion' || field.accordionSections || hasAccordionDataInForm) {
                logger.log('🎯 Processing accordion field:', fieldName, 'hasAccordionDataInForm:', hasAccordionDataInForm);

                let accordionData = [];

                // Try to use field definition first
                if (field.accordionSections) {
                  accordionData = formatAccordionData(field, formData);
                }

                // If no data from field definition, extract from form keys
                if (accordionData.length === 0 && hasAccordionDataInForm) {
                  logger.log('📦 Extracting accordion data from form keys for field:', fieldName);
                  const extractedData = extractAccordionDataFromFormKeys(formData, fieldsMap || {});

                  // Get data for this specific field
                  if (extractedData[fieldName]) {
                    accordionData = Object.values(extractedData[fieldName]);
                  }
                }

                const fieldLabel = field.labelName || field.label || formatLabel(field.field_name || field.name || '');
                if (accordionData && accordionData.length > 0) {
                  logger.log('✅ Rendering accordion data for field:', fieldName, 'sections:', accordionData.length);
                  await addAccordionToPdf(fieldLabel, accordionData);
                } else {
                  logger.log('⚠️ No accordion data found for field:', fieldName);
                }
                continue;
              }

              // Handle authorized_contacts field type
              // PDF Generation Rule: Include ALL contacts that have any data entered
              // Display role heading based on selected contact types (Emergency/Authorized Medical)
              const fieldType = field.type || field.fieldType || field.field_type;

              // Debug logging for authorized contacts
              logger.log(`🔍 Checking field: ${fieldName}, type: ${fieldType}`);

              if (fieldType === 'authorized_contacts') {
                const contacts = formData[fieldName];
                logger.log(`📋 Authorized Contacts field found: ${fieldName}`);
                logger.log(`📋 Contacts data:`, JSON.stringify(contacts, null, 2));

                if (contacts && Array.isArray(contacts) && contacts.length > 0) {
                  const lineHeight = 18;
                  let authorizedContactCount = 0;

                  // Process each contact - include ALL contacts that have data
                  for (let contactIndex = 0; contactIndex < contacts.length; contactIndex++) {
                    const contact = contacts[contactIndex];
                    if (!contact || typeof contact !== 'object') continue;

                    // Check if contact has any meaningful data
                    const hasData = contact.firstName || contact.lastName || contact.email || contact.phone;
                    if (!hasData) {
                      logger.log(`📋 Skipping contact ${contactIndex + 1} - No data entered`);
                      continue;
                    }

                    // Include all contacts that have data (both Emergency and Authorized)
                    authorizedContactCount++;

                    // Page break check
                    if (cursorY + lineHeight * 2 > pageHeight - 30) {
                      doc.addPage();
                      drawWatermark();
                      drawHeader();
                      cursorY = 100;
                    }

                    // Contact heading (e.g., "Emergency Contact 1:")
                    doc.setFont("Helvetica", "bold");
                    doc.setFontSize(12);
                    doc.setTextColor(0, 0, 0);

                    // Build role based on selected contact types
                    const roles = [];
                    if (contact.isEmergencyContact) roles.push('Emergency');
                    if (contact.isAuthorizedMedical) roles.push('Authorized Medical');

                    // Determine heading text based on roles
                    let headingText;
                    if (roles.length === 2) {
                      // Both selected: "Contact 1 (Emergency & Authorized Medical)"
                      headingText = `Contact ${authorizedContactCount} (${roles.join(' & ')})`;
                    } else if (roles.length === 1) {
                      // Single role: "Emergency Contact 1" or "Authorized Medical Contact 1"
                      headingText = `${roles[0]} Contact ${authorizedContactCount}`;
                    } else {
                      // No role selected (shouldn't happen, but fallback)
                      headingText = `Contact ${authorizedContactCount}`;
                    }

                    doc.text(`${headingText} :`, margin, cursorY);
                    cursorY += lineHeight;

                    // Contact details - simple label: value format
                    doc.setFont("Helvetica", "normal");
                    doc.setFontSize(11);

                    // Full Name
                    const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
                    if (fullName) {
                      if (cursorY + lineHeight > pageHeight - 30) {
                        doc.addPage();
                        drawWatermark();
                        drawHeader();
                        cursorY = 100;
                      }
                      doc.setFont("Helvetica", "bold");
                      doc.text('Name :', margin + 20, cursorY);
                      doc.setFont("Helvetica", "normal");
                      doc.text(fullName, margin + 120, cursorY);
                      cursorY += lineHeight;
                    }

                    // Email
                    if (contact.email) {
                      if (cursorY + lineHeight > pageHeight - 30) {
                        doc.addPage();
                        drawWatermark();
                        drawHeader();
                        cursorY = 100;
                      }
                      doc.setFont("Helvetica", "bold");
                      doc.text('Email :', margin + 20, cursorY);
                      doc.setFont("Helvetica", "normal");
                      doc.text(String(contact.email), margin + 120, cursorY);
                      cursorY += lineHeight;
                    }

                    // Phone
                    if (contact.phone) {
                      if (cursorY + lineHeight > pageHeight - 30) {
                        doc.addPage();
                        drawWatermark();
                        drawHeader();
                        cursorY = 100;
                      }
                      doc.setFont("Helvetica", "bold");
                      doc.text('Phone :', margin + 20, cursorY);
                      doc.setFont("Helvetica", "normal");
                      doc.text(String(contact.phone), margin + 120, cursorY);
                      cursorY += lineHeight;
                    }

                    // Relationship
                    if (contact.relationship) {
                      if (cursorY + lineHeight > pageHeight - 30) {
                        doc.addPage();
                        drawWatermark();
                        drawHeader();
                        cursorY = 100;
                      }
                      const relationshipText = contact.relationship === 'Other' && contact.otherRelationship
                        ? `Other - ${contact.otherRelationship}`
                        : contact.relationship;
                      doc.setFont("Helvetica", "bold");
                      doc.text('Relationship :', margin + 20, cursorY);
                      doc.setFont("Helvetica", "normal");
                      doc.text(String(relationshipText), margin + 120, cursorY);
                      cursorY += lineHeight;
                    }

                    cursorY += 5; // Small gap between contacts
                  }

                  // Log summary of authorized contacts in PDF
                  logger.log(`✅ PDF: Included ${authorizedContactCount} authorized contacts out of ${contacts.length} total`);
                }
                continue;
              }

              // Special handling for checkbox/radio fields with optionsWithConditional (Emergency/Authorized Contacts)
              if (field.optionsWithConditional && field.optionsWithConditional.length > 0) {
                const selectedValues = formData[fieldName];

                // Debug logging
                logger.log('🔍 Processing optionsWithConditional field:', fieldName);
                logger.log('📋 Selected values:', selectedValues);
                logger.log('📋 Options count:', field.optionsWithConditional.length);

                // Check if this is a contact-related field
                const isContactField = fieldName.toLowerCase().includes('contact') ||
                  (field.labelName && field.labelName.toLowerCase().includes('contact')) ||
                  field.optionsWithConditional.some(opt =>
                    opt.label && (opt.label.toLowerCase().includes('contact') ||
                                  opt.label.toLowerCase().includes('emergency') ||
                                  opt.label.toLowerCase().includes('authorized'))
                  );

                logger.log('📋 Is contact field:', isContactField);

                if (isContactField) {
                  const lineHeight = 18;
                  const labelValueLineHeight = 16;
                  let hasRenderedAnyContact = false;

                  // Process each option that has conditional inputs
                  field.optionsWithConditional.forEach((option, optionIndex) => {
                    const optionLabel = option.label || option.value;
                    const conditionalInputs = option.conditionalInputs || [];

                    logger.log(`📋 Option ${optionIndex}:`, optionLabel, 'inputs:', conditionalInputs.length);

                    // Check if this option is selected (if selectedValues exists)
                    let isSelected = true; // Default to true to check for data
                    if (selectedValues) {
                      isSelected = Array.isArray(selectedValues)
                        ? selectedValues.some(val => val === optionLabel || (val && val.startsWith && val.startsWith(`${optionLabel} -`)))
                        : selectedValues === optionLabel || (selectedValues.startsWith && selectedValues.startsWith(`${optionLabel} -`));
                    }

                    if (conditionalInputs.length === 0) {
                      return;
                    }

                    // Collect all conditional input values for this option
                    // Try multiple field name patterns
                    const contactData = [];
                    conditionalInputs.forEach((input, inputIndex) => {
                      // Pattern 1: with underscores (original)
                      const pattern1 = `${fieldName}_conditional_${optionIndex}_${inputIndex}`;
                      // Pattern 2: without underscores (after convertFieldNameToLowercase)
                      const pattern2 = `${fieldName}conditional${optionIndex}${inputIndex}`;
                      // Pattern 3: field might have been stored with different casing
                      const pattern3 = `${fieldName.toLowerCase()}conditional${optionIndex}${inputIndex}`;

                      const value = formData[pattern1] || formData[pattern2] || formData[pattern3];

                      logger.log(`  📋 Checking patterns for input ${inputIndex}:`, pattern1, pattern2, pattern3);
                      logger.log(`  📋 Found value:`, value);

                      if (value && value.toString().trim() !== '') {
                        contactData.push({
                          label: input.label || input.placeholder || `Field ${inputIndex + 1}`,
                          value: value
                        });
                      }
                    });

                    logger.log(`📋 Contact data for ${optionLabel}:`, contactData);

                    // Skip if no conditional data exists
                    if (contactData.length === 0) {
                      return;
                    }

                    hasRenderedAnyContact = true;

                    // Page break check for contact heading
                    if (cursorY + lineHeight * 2 > pageHeight - 30) {
                      doc.addPage();
                      drawWatermark();
                      drawHeader();
                      cursorY = 100;
                    }

                    // Contact heading (e.g., "Emergency Contact 1")
                    doc.setFont("Helvetica", "bold");
                    doc.setFontSize(12);
                    doc.text(optionLabel, margin, cursorY);
                    cursorY += lineHeight;

                    // Render each field as "Label: value"
                    doc.setFont("Helvetica", "normal");
                    doc.setFontSize(11);

                    for (const data of contactData) {
                      // Page break check
                      if (cursorY + labelValueLineHeight > pageHeight - 30) {
                        doc.addPage();
                        drawWatermark();
                        drawHeader();
                        cursorY = 100;
                      }

                      const labelText = `${data.label}: ${data.value}`;
                      const wrappedLines = doc.splitTextToSize(labelText, pageWidth - margin * 2 - 20);

                      for (const line of wrappedLines) {
                        if (cursorY + labelValueLineHeight > pageHeight - 30) {
                          doc.addPage();
                          drawWatermark();
                          drawHeader();
                          cursorY = 100;
                        }
                        doc.text(line, margin + 10, cursorY);
                        cursorY += labelValueLineHeight - 2;
                      }
                    }

                    // Add spacing between contact blocks
                    cursorY += 12;
                  });

                  if (hasRenderedAnyContact) {
                    continue; // Skip default rendering for this field
                  }
                }
              }

              // Special handling for address fields (composite field with subfields)
              if (field.type === 'address' || field.fieldType === 'address') {
                const streetValue = formData[`${fieldName}_street`] || formData[`${fieldName}street`];
                const suiteValue = formData[`${fieldName}_suite`] || formData[`${fieldName}suite`];
                const cityValue = formData[`${fieldName}_city`] || formData[`${fieldName}city`];
                const stateValue = formData[`${fieldName}_state`] || formData[`${fieldName}state`];
                const countryValue = formData[`${fieldName}_country`] || formData[`${fieldName}country`];

                // Check if any address data exists
                const hasAddressData = streetValue || suiteValue || cityValue || stateValue;

                if (hasAddressData) {
                  const fieldLabel = field.labelName || field.label || formatLabel(field.field_name || field.name || 'Address');

                  // Build formatted address string
                  const addressParts = [];
                  if (streetValue) addressParts.push(streetValue);
                  if (suiteValue) addressParts.push(`Suite/Apt: ${suiteValue}`);
                  if (cityValue) addressParts.push(cityValue);
                  if (stateValue) addressParts.push(stateValue);
                  if (countryValue && countryValue !== 'US') addressParts.push(countryValue);

                  const formattedAddress = addressParts.join(', ');

                  if (formattedAddress) {
                    await addLabelValue(fieldLabel, formattedAddress);
                  }
                }
                continue;
              }

              // Check if this field has conditional inputs in formData (fallback detection)
              // Pattern with underscores: fieldname_conditional_0_0
              // Pattern without underscores: fieldnameconditional00
              const conditionalPattern = new RegExp(`^${fieldName}_conditional_(\\d+)_(\\d+)$`, 'i');
              const conditionalPatternAlt = new RegExp(`^${fieldName}conditional(\\d+)(\\d+)$`, 'i');
              // Also check for pattern where digits might run together
              const conditionalPatternAlt2 = new RegExp(`^${fieldName}conditional(\\d)(\\d)$`, 'i');
              const hasConditionalData = Object.keys(formData).some(key =>
                conditionalPattern.test(key) || conditionalPatternAlt.test(key) || conditionalPatternAlt2.test(key)
              );

              logger.log('🔍 Fallback check for field:', fieldName);
              logger.log('🔍 Has conditional data:', hasConditionalData);
              if (hasConditionalData) {
                logger.log('🔍 Matching keys:', Object.keys(formData).filter(key =>
                  conditionalPattern.test(key) || conditionalPatternAlt.test(key) || conditionalPatternAlt2.test(key)
                ));
              }

              // If field has conditional data and is contact-related, render structured format
              if (hasConditionalData) {
                const isContactField = fieldName.toLowerCase().includes('contact') ||
                  (field.labelName && field.labelName.toLowerCase().includes('contact'));

                if (isContactField) {
                  const selectedValues = formData[fieldName];
                  const lineHeight = 18;
                  const labelValueLineHeight = 16;

                  // Group conditional fields by option index
                  const conditionalGroups = {};
                  Object.keys(formData).forEach(key => {
                    let match = key.match(conditionalPattern);
                    if (!match) match = key.match(conditionalPatternAlt);
                    if (!match) match = key.match(conditionalPatternAlt2);
                    if (match) {
                      const [, optionIndex, inputIndex] = match;
                      if (!conditionalGroups[optionIndex]) {
                        conditionalGroups[optionIndex] = [];
                      }
                      conditionalGroups[optionIndex].push({
                        inputIndex: parseInt(inputIndex),
                        value: formData[key],
                        key: key
                      });
                    }
                  });

                  logger.log('📋 Conditional groups:', conditionalGroups);

                  // Get labels from optionsWithConditional if available, otherwise use selected values
                  const options = field.optionsWithConditional || field.options || [];

                  Object.keys(conditionalGroups).sort((a, b) => parseInt(a) - parseInt(b)).forEach(optionIndex => {
                    const group = conditionalGroups[optionIndex].sort((a, b) => a.inputIndex - b.inputIndex);

                    // Skip if all values are empty
                    const hasValues = group.some(item => item.value && item.value.toString().trim() !== '');
                    if (!hasValues) return;

                    // Get option label
                    let optionLabel = `Contact ${parseInt(optionIndex) + 1}`;
                    const optionDef = options[parseInt(optionIndex)];
                    if (optionDef) {
                      optionLabel = optionDef.label || optionDef.value || optionLabel;
                    } else if (Array.isArray(selectedValues) && selectedValues[parseInt(optionIndex)]) {
                      // Try to get from selected values array
                      const selectedVal = selectedValues.find((val, idx) => {
                        // Match by position or by checking if it contains the index
                        return idx === parseInt(optionIndex) ||
                               val.includes(`${parseInt(optionIndex) + 1}`);
                      });
                      if (selectedVal) optionLabel = selectedVal;
                    }

                    // Page break check
                    if (cursorY + lineHeight * 2 > pageHeight - 30) {
                      doc.addPage();
                      drawWatermark();
                      drawHeader();
                      cursorY = 100;
                    }

                    // Contact heading
                    doc.setFont("Helvetica", "bold");
                    doc.setFontSize(12);
                    doc.text(optionLabel, margin, cursorY);
                    cursorY += lineHeight;

                    // Render each field
                    doc.setFont("Helvetica", "normal");
                    doc.setFontSize(11);

                    group.forEach(item => {
                      if (!item.value || item.value.toString().trim() === '') return;

                      // Get label from optionsWithConditional if available
                      let inputLabel = `Field ${item.inputIndex + 1}`;
                      if (optionDef && optionDef.conditionalInputs && optionDef.conditionalInputs[item.inputIndex]) {
                        inputLabel = optionDef.conditionalInputs[item.inputIndex].label ||
                                    optionDef.conditionalInputs[item.inputIndex].placeholder ||
                                    inputLabel;
                      }

                      // Page break check
                      if (cursorY + labelValueLineHeight > pageHeight - 30) {
                        doc.addPage();
                        drawWatermark();
                        drawHeader();
                        cursorY = 100;
                      }

                      const labelText = `${inputLabel}: ${item.value}`;
                      const wrappedLines = doc.splitTextToSize(labelText, pageWidth - margin * 2 - 20);

                      for (const line of wrappedLines) {
                        doc.text(line, margin + 10, cursorY);
                        cursorY += labelValueLineHeight - 2;
                      }
                    });

                    cursorY += 12;
                  });

                  continue; // Skip default rendering
                }
              }

              const fieldValue = formData[fieldName];
              const fieldLabel = field.labelName || field.label || formatLabel(field.field_name || field.name || '');

              if (!shouldSkipField(fieldLabel, fieldValue, fieldName)) {
                await addLabelValue(fieldLabel, fieldValue);
              }
            }

            cursorY += 15;
          }
        }
      }

      // ✅ Fallback: Extract accordion data by scanning form keys
      // This handles cases where accordionSections might not be in fieldsMap AND field was not in any module
      logger.log('🔍 Checking for unprocessed accordion data...');
      logger.log('📋 Form data keys with "section":', Object.keys(formData).filter(k => k.includes('section')));

      const extractedAccordionData = extractAccordionDataFromFormKeys(formData, fieldsMap || {});

      logger.log('✅ Extracted accordion data:', extractedAccordionData);

      // Track which fields have been processed
      const processedAccordionFields = new Set();
      if (fieldsMap) {
        for (const fields of Object.values(fieldsMap)) {
          for (const field of fields) {
            const fieldName = toLowercase(field.labelName || field.field_name || field.name || '');
            if (fieldName && (field.fieldType === 'accordion' || field.type === 'accordion' || field.accordionSections)) {
              processedAccordionFields.add(fieldName);
            }
          }
        }
      }

      logger.log('🔒 Already processed accordion fields:', Array.from(processedAccordionFields));

      if (Object.keys(extractedAccordionData).length > 0) {
        // Check if we have unprocessed accordion data
        let hasUnprocessedAccordion = false;

        for (const [baseFieldName, sections] of Object.entries(extractedAccordionData)) {
          // Skip if this field was already processed in modules
          if (processedAccordionFields.has(baseFieldName)) {
            logger.log('⏭️ Skipping already processed field:', baseFieldName);
            continue;
          }

          for (const [sectionIndex, sectionData] of Object.entries(sections)) {
            if (sectionData.checkedItems.length > 0) {
              hasUnprocessedAccordion = true;
              break;
            }
          }
          if (hasUnprocessedAccordion) break;
        }

        if (hasUnprocessedAccordion) {
          logger.log('📝 Rendering unprocessed accordion data at end of PDF');
          // Render extracted accordion data
          for (const [baseFieldName, sections] of Object.entries(extractedAccordionData)) {
            // Skip if already processed
            if (processedAccordionFields.has(baseFieldName)) {
              continue;
            }

            for (const [sectionIndex, sectionData] of Object.entries(sections)) {
              if (sectionData.checkedItems.length > 0) {
                const lineHeight = 18;

                // Section title as question (bold)
                if (cursorY + lineHeight > pageHeight - 30) {
                  doc.addPage();
                  drawWatermark();
                  drawHeader();
                  cursorY = 100;
                }

                doc.setFont("Helvetica", "bold");
                doc.setFontSize(12);

                const sectionTitleLines = doc.splitTextToSize(sectionData.sectionTitle, pageWidth - margin * 2);
                for (const line of sectionTitleLines) {
                  if (cursorY + lineHeight > pageHeight - 30) {
                    doc.addPage();
                    drawWatermark();
                    drawHeader();
                    cursorY = 100;
                  }
                  doc.text(line, margin, cursorY);
                  cursorY += lineHeight;
                }

                // Checked items - comma separated
                const checkedLabels = sectionData.checkedItems.map(item => item.label);
                if (checkedLabels.length > 0) {
                  doc.setFont("Helvetica", "normal");
                  doc.setFontSize(11);

                  const checkedText = checkedLabels.join(', ');
                  const wrappedChecked = doc.splitTextToSize(checkedText, pageWidth - margin * 2);

                  for (const line of wrappedChecked) {
                    if (cursorY + lineHeight > pageHeight - 30) {
                      doc.addPage();
                      drawWatermark();
                      drawHeader();
                      cursorY = 100;
                    }
                    doc.text(line, margin, cursorY);
                    cursorY += lineHeight - 2;
                  }

                  // Nested values
                  for (const item of sectionData.checkedItems) {
                    if (item.nestedValues && item.nestedValues.length > 0) {
                      for (const nested of item.nestedValues) {
                        if (cursorY + lineHeight > pageHeight - 30) {
                          doc.addPage();
                          drawWatermark();
                          drawHeader();
                          cursorY = 100;
                        }

                        doc.setFont("Helvetica", "italic");
                        doc.setFontSize(10);
                        const nestedText = `${item.label} - ${nested.label}: ${nested.value}`;
                        const wrappedLines = doc.splitTextToSize(nestedText, pageWidth - margin * 2 - 30);
                        for (const line of wrappedLines) {
                          doc.text(line, margin + 20, cursorY);
                          cursorY += lineHeight - 4;
                        }
                      }
                    }
                  }
                }

                cursorY += 15;
              }
            }
          }
        }
      }

      const addFooter = () => {
        doc.setFillColor(54, 56, 69);
        doc.rect(0, pageHeight - 10, pageWidth, 10, "F");
      };

      // Check if last page is essentially empty (cursor near top means no content was added)
      // If cursorY is less than 150 (just header area), and we have more than 1 page,
      // the last page is likely empty from a premature page break
      let totalPages = doc.internal.getNumberOfPages();

      // Remove empty trailing page if cursor is near top (no real content added)
      if (totalPages > 1 && cursorY <= 120) {
        logger.log(`[PDF] Removing empty trailing page. cursorY=${cursorY}, totalPages=${totalPages}`);
        doc.deletePage(totalPages);
        totalPages = doc.internal.getNumberOfPages();
      }

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        if (i > 1) {
          drawWatermark();
          drawHeader();
        }
        addFooter();
      }

      const pdfBlob = doc.output("blob");
      resolve(pdfBlob);
    } catch (error) {
      logger.error("❌ PDF generation error:", error);
      reject(error);
    }
  });
};

const loadImageToBase64 = (url) => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        logger.warn("Failed to convert image to base64:", e);
        resolve(null);
      }
    };
    img.onerror = () => {
      logger.warn("Failed to load image:", url);
      resolve(null); // Resolve with null instead of rejecting to allow PDF generation to continue
    };
    // Add timeout for slow-loading images
    setTimeout(() => {
      if (!img.complete) {
        logger.warn("Image load timeout:", url);
        resolve(null);
      }
    }, 10000);
    img.src = url;
  });
};